import { useLogger } from '@omnia/ui/src/lib/hooks/useLogger'
import { type FC, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom-v7'
import { api } from '../../services/api'
import type { MapfreDetails } from './models'
import { MAPFRE_TOKEN_STORAGE_KEY } from './models'

/**
 * Logic contained within a component as it uses hooks to get and update the search parameters, which would otherwise
 * cause a flicker when the mapfre parameter is removed as this logic needs to happen globally.
 *
 * @returns Nothing
 */
export const CaptureMapfreToken: FC = () => {
    const logger = useLogger('CaptureMapfreToken')
    logger.trace('Rendered')

    const [searchParams, setSearchParams] = useSearchParams()

    useEffect(() => {
        void (async () => {
            const mapfreToken = searchParams.get('mapfre')

            if (!mapfreToken) return

            logger.info('Captured Mapfre token from query parameters.', { mapfreToken })

            try {
                const mapfreDetails = await api.GET<MapfreDetails>(api.Mapfre(mapfreToken))

                if (mapfreDetails?.error) {
                    throw new Error(mapfreDetails.error)
                }

                logger.debug('Successfully got mapfre details. Saving to local storage.', mapfreDetails)

                localStorage.setItem(MAPFRE_TOKEN_STORAGE_KEY, JSON.stringify(mapfreDetails))
            } catch (error: unknown) {
                logger.error('Failed to get mapfre details for provided token.', { mapfreToken, error })
            } finally {
                logger.trace('Removing mapfre query parameter')

                setSearchParams(
                    params => {
                        params.delete('mapfre')
                        return params
                    },
                    { replace: true }
                )
            }
        })()
        // eslint-disable-next-line react-hooks/exhaustive-deps -- This is fine
    }, [searchParams])

    return <></>
}
